import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { useToast } from '../components/ui/use-toast';
import { useUser } from '../contexts/UserContext';
import { reportsService, SellerTrainingProgress } from '../services/reportsService';
import { Spinner } from '../components/ui/Spinner';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

export const SellerTrainingProgressReport: React.FC = () => {
  const [reportData, setReportData] = useState<SellerTrainingProgress[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState({
    seller: '',
    trainer: '',
    module: ''
  });
  // Applied filters - these are sent to API for pagination consistency
  const [appliedFilters, setAppliedFilters] = useState({
    seller: '',
    trainer: '',
    module: ''
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize] = useState(50);
  const { toast } = useToast();
  const { currentUser } = useUser();

  const [isInitialLoad, setIsInitialLoad] = useState(true);

  useEffect(() => {
    // Always fetch data, regardless of distributor code
    console.log('Current user changed:', currentUser);
    console.log('Distributor code:', currentUser?.distributorCode);
    setIsInitialLoad(true);
    fetchReportData();
  }, [currentUser]);

  useEffect(() => {
    if (!isInitialLoad) {
      console.log('Page changed to:', currentPage, 'Fetching data...');
      fetchReportDataForPage(currentPage);
    }
    if (isInitialLoad) {
      setIsInitialLoad(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  const fetchReportData = async (filtersToUse = appliedFilters) => {
    try {
      setLoading(true);
      setError(null);
      setCurrentPage(1);
      setReportData([]);

      console.log('Fetching data:', {
        distributorCode: currentUser?.distributorCode || 'ALL',
        page: 1,
        pageSize,
        filters: filtersToUse
      });

      const response = await reportsService.getSellerTrainingProgress(
        currentUser?.distributorCode,
        1,
        pageSize,
        filtersToUse
      );
      
      console.log('Data received:', { count: response.data.length, totalCount: response.totalCount, totalPages: response.totalPages });
      
      setReportData(response.data);
      setTotalCount(response.totalCount);
      setTotalPages(response.totalPages);
      console.log('Total count:', response.totalCount, 'Total pages:', response.totalPages);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch report data');
      toast({
        title: "Error",
        description: "Failed to fetch report data. Please try again later.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchReportDataForPage = async (page: number) => {
    try {
      setLoading(true);
      setError(null);

      console.log('Fetching data:', {
        distributorCode: currentUser?.distributorCode || 'ALL',
        page,
        pageSize,
        filters: appliedFilters
      });

      const response = await reportsService.getSellerTrainingProgress(
        currentUser?.distributorCode,
        page,
        pageSize,
        appliedFilters
      );
      
      console.log('Data received:', { count: response.data.length, totalCount: response.totalCount, totalPages: response.totalPages });
      
      setReportData(response.data);
      setTotalCount(response.totalCount);
      setTotalPages(response.totalPages);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch report data');
      toast({
        title: "Error",
        description: "Failed to fetch report data. Please try again later.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = () => {
    const newFilters = { ...filters };
    setAppliedFilters(newFilters);
    setCurrentPage(1);
    fetchReportData(newFilters);
  };

  const handleClearFilters = () => {
    const emptyFilters = { seller: '', trainer: '', module: '' };
    setFilters(emptyFilters);
    setAppliedFilters(emptyFilters);
    setCurrentPage(1);
    fetchReportData(emptyFilters);
  };

  const handleExport = async () => {
    try {
      setLoading(true);
      
      // Use the export method to get all records based on applied filters
      const exportData = await reportsService.exportSellerTrainingProgress(
        currentUser?.distributorCode,
        appliedFilters
      );

      if (exportData && exportData.length > 0) {
        // Prepare columns for export
        const columns = [
          { label: 'Distributor Code', key: 'distributorCode' },
          { label: 'Distributor Name', key: 'distributorName' },
          { label: 'Site', key: 'site' },
          { label: 'Username', key: 'username' },
          { label: 'Role', key: 'role' },
          { label: 'Location', key: 'location' },
          { label: 'Trainer Name', key: 'trainername' },
          { label: 'Module Name', key: 'moduleName' },
          { label: 'Module Status', key: 'moduleStatus' },
          { label: 'Classroom Completed', key: 'quizCompleted' },
          { label: 'Classroom Score', key: 'quizScore' },
          { label: 'Classroom Points', key: 'quizPoints' },
          { label: 'Classroom Points Configured', key: 'quizPointsConfigured' },
          { label: 'Reels Viewed', key: 'reelsViewed' },
          { label: 'Reels Configured', key: 'reelsConfigured' },
          { label: 'Reels Points', key: 'reelsPoints' },
          { label: 'Reels Points Configured', key: 'reelsPointsConfigured' },
          { label: 'In Market Completed', key: 'inMarketCompleted' },
          { label: 'In Market Points', key: 'inMarketPoints' },
          { label: 'In Market Points Configured', key: 'inMarketPointsConfigured' },
          { label: 'Mock Call Completed', key: 'mockCallCompleted' },
          { label: 'Mock Call Points', key: 'mockCallPoints' },
          { label: 'Mock Call Points Configured', key: 'mockCallPointsConfigured' }
        ];

        // Map data to only include the columns specified
        const mappedData = exportData.map(row => {
          const filtered: any = {};
          columns.forEach(col => {
            let value = (row as any)[col.key];
            
            // Format Classroom Completed as YES/NO
            if (col.key === 'quizCompleted') {
              value = value >= 1 ? 'YES' : 'NO';
            }
            
            // Format Module Status to match page display
            if (col.key === 'moduleStatus') {
              value = value.replace('_', ' ').toUpperCase();
            }
            
            filtered[col.label] = value;
          });
          return filtered;
        });

        // Create Excel file
        const worksheet = XLSX.utils.json_to_sheet(mappedData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
        const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
        
        // Download the file
        saveAs(blob, 'SellerTrainingProgressReport.xlsx');
        
        toast({
          title: "Success",
          description: `Exported ${exportData.length} records successfully`,
        });
      } else {
        toast({
          title: "No Data",
          description: "No data available for export",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Export error:', error);
      toast({
        title: "Export Failed",
        description: "Failed to export data. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'in_progress':
        return 'bg-yellow-100 text-yellow-800';
      case 'not_started':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-blue-100 text-blue-800';
    }
  };

  const getTotalPoints = (item: SellerTrainingProgress) => {
    return item.quizPoints + item.reelsPoints + item.inMarketPoints + item.mockCallPoints;
  };

  if (loading) {
    return (
      <div className="container mx-auto p-6">
        <Card>
          <CardContent className="flex items-center justify-center h-32">
            <Spinner label="Loading report data..." />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto p-6">
        <Card>
          <CardContent className="flex items-center justify-center h-32">
            <div className="text-center">
              <p className="text-red-500 mb-4">{error}</p>
              <Button onClick={() => fetchReportData()}>Retry</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <div className="flex items-center space-x-2">
              <CardTitle>Seller Training Progress Report</CardTitle>
              {!currentUser?.distributorCode && (
                <Badge variant="outline" className="ml-2">
                  All Distributors
                </Badge>
              )}
            </div>
          <div className="flex gap-2">
            <Button onClick={handleFilterChange} disabled={loading}>
              Apply Filters
            </Button>
            <Button onClick={handleClearFilters} variant="outline" disabled={loading}>
              Clear Filters
            </Button>
            <Button onClick={handleExport} disabled={loading} variant="secondary">
              Export All Data
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="space-y-2">
              <Label htmlFor="seller-filter">Seller</Label>
              <Input
                id="seller-filter"
                placeholder="Enter seller username..."
                value={filters.seller}
                onChange={(e) => setFilters(prev => ({ ...prev, seller: e.target.value }))}
                onKeyDown={(e) => e.key === 'Enter' && handleFilterChange()}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="trainer-filter">Trainer</Label>
              <Input
                id="trainer-filter"
                placeholder="Enter trainer username..."
                value={filters.trainer}
                onChange={(e) => setFilters(prev => ({ ...prev, trainer: e.target.value }))}
                onKeyDown={(e) => e.key === 'Enter' && handleFilterChange()}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="module-filter">Module</Label>
              <Input
                id="module-filter"
                placeholder="Enter module name..."
                value={filters.module}
                onChange={(e) => setFilters(prev => ({ ...prev, module: e.target.value }))}
                onKeyDown={(e) => e.key === 'Enter' && handleFilterChange()}
              />
            </div>
          </div>
          <div className="flex justify-end items-center mb-4 gap-2">
            {/* Removed Apply Filters and Clear Filters buttons from here */}
          </div>

          {/* Summary Info */}
          <div className="mb-4 text-center">
            <p className="text-sm text-muted-foreground">
              {(() => {
                const startRecord = totalCount > 0 ? (currentPage - 1) * pageSize + 1 : 0;
                const endRecord = Math.min(currentPage * pageSize, totalCount);
                const rangeText = totalCount > 0 
                  ? `Showing ${startRecord} to ${endRecord} of ${totalCount} total records`
                  : 'No records found';
                return currentUser?.distributorCode 
                  ? `${rangeText} for distributor ${currentUser.distributorCode}`
                  : `${rangeText} across all distributors`;
              })()}
            </p>
          </div>

          {/* Report Table */}
          <div className="max-h-[60vh] overflow-y-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Seller</TableHead>
                  <TableHead>Trainer</TableHead>
                  <TableHead>Module</TableHead>
                  <TableHead>Status</TableHead>
                            <TableHead>Classroom Completed</TableHead>
          <TableHead>Classroom Score</TableHead>
          <TableHead>Classroom Pts</TableHead>
                  <TableHead>Reels</TableHead>
                  <TableHead>Reels Pts</TableHead>
                  <TableHead>In Market Pts</TableHead>
                  <TableHead>Mock Call Pts</TableHead>
                  <TableHead>Total Points</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {reportData.length === 0 ? (
                  <TableRow>
                                      <TableCell colSpan={12} className="text-center text-gray-500 py-8">
                    No report data found
                  </TableCell>
                  </TableRow>
                ) : (
                  reportData.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <div>
                          <div className="font-medium">{item.username}</div>
                          <div className="text-sm text-muted-foreground">{item.location || 'N/A'}</div>
                        </div>
                      </TableCell>
                      <TableCell className="font-medium">{item.trainername}</TableCell>
                      <TableCell>
                        <div>
                          <div className="font-medium">{item.moduleName}</div>
                          <div className="text-sm text-muted-foreground">{item.site}</div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={getStatusColor(item.moduleStatus)}>
                          {item.moduleStatus.replace('_', ' ').toUpperCase()}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="text-center">
                          <div className="font-medium">{item.quizCompleted ? 'Yes' : 'No'}</div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-center">
                          <div className="font-medium">{item.quizScore || 0}</div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-center">
                          <div className="font-medium">{item.quizPoints}/{item.quizPointsConfigured > 0 ? item.quizPointsConfigured : '0'}</div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-center">
                          <div className="font-medium">{item.reelsViewed}/{item.reelsConfigured}</div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-center">
                          <div className="font-medium">{item.reelsPoints}/{item.reelsPointsConfigured} pts</div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-center">
                          <div className="font-medium">{item.inMarketPoints}/{item.inMarketPointsConfigured} pts</div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-center">
                          <div className="font-medium">{item.mockCallPoints}/{item.mockCallPointsConfigured} pts</div>
                        </div>
                      </TableCell>
                      <TableCell className="font-bold text-center">
                        {getTotalPoints(item)}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="flex justify-center items-center mt-6 gap-2">
              <Button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
              >
                Previous
              </Button>
              <span>Page {currentPage} of {totalPages}</span>
              <Button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
              >
                Next
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}; 